import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MediaPicker } from "@/components/MediaPicker";
import { Loader2, Sparkles, GripVertical, Wand2 } from "lucide-react";

const DEFAULT_OVERLAY = {
  show_logo: true, show_phone: true, show_website: true,
  logo_x: 0.04, logo_y: 0.04, logo_scale: 0.18, logo_opacity: 1.0,
  text_x: 0.04, text_y: 0.90, text_scale: 1.0, text_opacity: 1.0,
  show_banner: false, banner_text: "", banner_x: 0.08, banner_y: 0.70,
  banner_scale: 1.6, banner_opacity: 1.0, banner_bg: "#111827", banner_fg: "#FFFFFF",
};

function DraggableHandle({ label, x, y, containerRef, onMove }) {
  const dragging = useRef(false);

  const onPointerDown = (e) => {
    e.preventDefault();
    dragging.current = true;
    const move = (ev) => {
      if (!dragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const nx = Math.min(Math.max((ev.clientX - rect.left) / rect.width, 0), 1);
      const ny = Math.min(Math.max((ev.clientY - rect.top) / rect.height, 0), 1);
      onMove(nx, ny);
    };
    const up = () => {
      dragging.current = false;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <div
      onPointerDown={onPointerDown}
      data-testid={`drag-handle-${label.toLowerCase()}`}
      className="absolute cursor-grab active:cursor-grabbing bg-[#0055FF] text-white text-[10px] font-bold px-2 py-1 rounded-sm flex items-center gap-1 shadow-lg select-none touch-none"
      style={{ left: `${x * 100}%`, top: `${y * 100}%`, transform: "translate(0, 0)" }}
    >
      <GripVertical className="h-3 w-3" /> {label}
    </div>
  );
}

export default function CreativeStudioPage() {
  const { activeTenantId, activeTenant } = useAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [referenceMediaId, setReferenceMediaId] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [variantCount, setVariantCount] = useState(2);
  const [generating, setGenerating] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [presets, setPresets] = useState({});
  const [stylePreset, setStylePreset] = useState("none");
  const [active, setActive] = useState(null);
  const [overlay, setOverlay] = useState(DEFAULT_OVERLAY);
  const [composedNonce, setComposedNonce] = useState(0);
  const [saving, setSaving] = useState(false);
  const containerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/images");
      setImages(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (activeTenantId) load(); }, [activeTenantId, load]);
  useEffect(() => { api.get("/images/style-presets").then((r) => setPresets(r.data)).catch(() => {}); }, []);

  const suggestPrompt = async () => {
    if (!prompt.trim()) return toast.error("Escribí un tema breve (ej. attic insulation Brooklyn) para poder sugerir");
    setSuggesting(true);
    try {
      const res = await api.post("/images/suggest-prompt", {
        topic: prompt, style_preset: stylePreset === "none" ? null : stylePreset,
      });
      setPrompt(res.data.prompt);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo sugerir un prompt");
    } finally {
      setSuggesting(false);
    }
  };

  const generate = async () => {
    if (!referenceMediaId) return toast.error("Elegí una foto de referencia de tu biblioteca");
    if (!prompt.trim()) return toast.error("Describí qué querés que se genere");
    setGenerating(true);
    try {
      const res = await api.post("/images", {
        reference_media_id: referenceMediaId, prompt, variant_count: variantCount,
        style_preset: stylePreset === "none" ? null : stylePreset,
      });
      toast.success("Imágenes generadas");
      setImages((prev) => [res.data, ...prev]);
      selectImage(res.data);
      setPickerOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar la imagen");
    } finally {
      setGenerating(false);
    }
  };

  const selectImage = (img) => {
    setActive(img);
    setOverlay(img.overlay || DEFAULT_OVERLAY);
    setComposedNonce((n) => n + 1);
  };

  const selectVariant = async (idx) => {
    if (!active) return;
    const res = await api.put(`/images/${active.id}/overlay`, { overlay, selected_variant_index: idx });
    setActive(res.data);
    setComposedNonce((n) => n + 1);
  };

  const saveOverlay = async (nextOverlay) => {
    if (!active) return;
    setOverlay(nextOverlay);
    setSaving(true);
    try {
      const res = await api.put(`/images/${active.id}/overlay`, { overlay: nextOverlay });
      setActive(res.data);
      setComposedNonce((n) => n + 1);
    } finally {
      setSaving(false);
    }
  };

  const composedUrl = active ? `${active.composed_url}?v=${composedNonce}` : null;
  const rawUrl = active && active.variant_urls?.[active.selected_variant_index || 0];

  return (
    <div className="space-y-6" data-testid="creative-studio-page">
      <p className="text-sm text-[#71717A] max-w-2xl">
        Generación de imágenes desde una foto real de tu biblioteca (nunca desde cero) + logo, teléfono y
        sitio web que podés arrastrar sobre la foto. El archivo original nunca se modifica.
      </p>

      <Card className="rounded-sm border-[#E4E4E7] shadow-none p-4 space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-[#71717A]">Foto de referencia (biblioteca de medios)</Label>
            <Button variant="ghost" size="sm" onClick={() => setPickerOpen((v) => !v)} data-testid="toggle-media-picker-button" className="text-xs h-7">
              {pickerOpen ? "Ocultar" : "Elegir foto"}
            </Button>
          </div>
          {pickerOpen && <MediaPicker selected={referenceMediaId} onSelect={setReferenceMediaId} />}
          {!pickerOpen && referenceMediaId && <p className="text-xs text-[#10B981]">Foto de referencia seleccionada ✓</p>}
        </div>
        <Textarea data-testid="image-prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Convertí esta foto en una imagen promocional lista para publicar, luminosa y prolija, mostrando el trabajo de aislamiento terminado" rows={3} />
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Select value={stylePreset} onValueChange={setStylePreset}>
              <SelectTrigger data-testid="style-preset-select" className="h-8 w-48 text-xs"><SelectValue placeholder="Estilo (opcional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin estilo</SelectItem>
                {Object.keys(presets).map((key) => (
                  <SelectItem key={key} value={key}>
                    {key === "professional_clean" ? "Professional Clean" : key === "vibrant_energy" ? "Vibrant Energy"
                      : key === "warm_inviting" ? "Warm Inviting" : key === "technical_detail" ? "Technical Detail" : key}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={suggestPrompt} disabled={suggesting} className="h-8 text-xs" data-testid="suggest-prompt-button">
              {suggesting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Wand2 className="h-3.5 w-3.5 mr-1" />} Sugerir
            </Button>
          </div>
          <div className="flex items-center gap-2 text-sm text-[#71717A]">
            <span>Variantes:</span>
            {[1, 2, 3, 4].map((n) => (
              <button key={n} onClick={() => setVariantCount(n)} data-testid={`variant-count-${n}`}
                className={`h-7 w-7 rounded-sm border text-xs ${variantCount === n ? "bg-[#0055FF] text-white border-[#0055FF]" : "border-[#E4E4E7]"}`}>{n}</button>
            ))}
          </div>
          <Button
            onClick={generate} disabled={generating || activeTenant?.status === "suspended"}
            title={activeTenant?.status === "suspended" ? "Cuenta suspendida" : undefined}
            className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md" data-testid="generate-image-button"
          >
            {generating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />} Generar imagen
          </Button>
        </div>
      </Card>

      {active && (
        <Card className="rounded-sm border-[#E4E4E7] shadow-none p-4 space-y-4" data-testid="image-editor-card">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {active.variant_urls.map((url, idx) => (
              <button key={idx} onClick={() => selectVariant(idx)} data-testid={`select-variant-${idx}`}
                className={`shrink-0 rounded-sm overflow-hidden border-2 ${idx === (active.selected_variant_index || 0) ? "border-[#0055FF]" : "border-transparent"}`}>
                <img src={url} alt={`variante ${idx}`} className="h-20 w-28 object-cover" />
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-[#71717A] mb-1">Arrastrá el logo y el texto sobre la foto (posición se guarda sola)</p>
              <div ref={containerRef} className="relative rounded-sm overflow-hidden border border-[#E4E4E7]" data-testid="overlay-drag-container">
                {rawUrl && <img src={rawUrl} alt="variante seleccionada" className="w-full block" />}
                <DraggableHandle label="LOGO" x={overlay.logo_x} y={overlay.logo_y} containerRef={containerRef}
                  onMove={(x, y) => saveOverlay({ ...overlay, logo_x: x, logo_y: y })} />
                <DraggableHandle label="TEXTO" x={overlay.text_x} y={overlay.text_y} containerRef={containerRef}
                  onMove={(x, y) => saveOverlay({ ...overlay, text_x: x, text_y: y })} />
                {overlay.show_banner && (
                  <DraggableHandle label="BANNER" x={overlay.banner_x} y={overlay.banner_y} containerRef={containerRef}
                    onMove={(x, y) => saveOverlay({ ...overlay, banner_x: x, banner_y: y })} />
                )}
              </div>
            </div>
            <div>
              <p className="text-xs text-[#71717A] mb-1">Resultado final (con marca superpuesta) {saving && <Loader2 className="inline h-3 w-3 animate-spin ml-1" />}</p>
              <img src={composedUrl} alt="compuesta" className="w-full rounded-sm border border-[#E4E4E7]" data-testid="composed-preview-image" />
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 pt-2 border-t border-[#F4F4F5]">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Mostrar logo</Label>
              <Switch checked={overlay.show_logo} onCheckedChange={(v) => saveOverlay({ ...overlay, show_logo: v })} data-testid="toggle-show-logo" />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Mostrar teléfono</Label>
              <Switch checked={overlay.show_phone} onCheckedChange={(v) => saveOverlay({ ...overlay, show_phone: v })} data-testid="toggle-show-phone" />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Mostrar sitio web</Label>
              <Switch checked={overlay.show_website} onCheckedChange={(v) => saveOverlay({ ...overlay, show_website: v })} data-testid="toggle-show-website" />
            </div>
            <div className="col-span-full sm:col-span-1 space-y-1">
              <Label className="text-xs text-[#71717A]">Tamaño del logo</Label>
              <Slider value={[overlay.logo_scale]} min={0.05} max={0.4} step={0.01} onValueChange={([v]) => saveOverlay({ ...overlay, logo_scale: v })} data-testid="slider-logo-scale" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#71717A]">Opacidad del logo</Label>
              <Slider value={[overlay.logo_opacity]} min={0.1} max={1} step={0.05} onValueChange={([v]) => saveOverlay({ ...overlay, logo_opacity: v })} data-testid="slider-logo-opacity" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#71717A]">Tamaño del texto</Label>
              <Slider value={[overlay.text_scale]} min={0.5} max={2} step={0.1} onValueChange={([v]) => saveOverlay({ ...overlay, text_scale: v })} data-testid="slider-text-scale" />
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 pt-2 border-t border-[#F4F4F5]">
            <div className="flex items-center justify-between col-span-full sm:col-span-1">
              <Label className="text-xs">Mostrar banner</Label>
              <Switch checked={overlay.show_banner} onCheckedChange={(v) => saveOverlay({ ...overlay, show_banner: v })} data-testid="banner-toggle-show" />
            </div>
            <div className="col-span-full sm:col-span-2 space-y-1">
              <Label className="text-xs text-[#71717A]">Texto del banner (ej. "Free Estimates")</Label>
              <Input value={overlay.banner_text} maxLength={60} placeholder="20% OFF ATTIC INSULATION"
                onChange={(e) => setOverlay({ ...overlay, banner_text: e.target.value })}
                onBlur={() => saveOverlay(overlay)} data-testid="banner-text-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#71717A]">Tamaño del banner</Label>
              <Slider value={[overlay.banner_scale]} min={0.8} max={3} step={0.1} onValueChange={([v]) => saveOverlay({ ...overlay, banner_scale: v })} data-testid="banner-slider-scale" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#71717A]">Opacidad del banner</Label>
              <Slider value={[overlay.banner_opacity]} min={0.1} max={1} step={0.05} onValueChange={([v]) => saveOverlay({ ...overlay, banner_opacity: v })} data-testid="banner-slider-opacity" />
            </div>
          </div>
        </Card>
      )}

      <div>
        <h3 className="font-heading text-sm font-bold mb-3">Imágenes generadas</h3>
        <Card className="rounded-sm border-[#E4E4E7] shadow-none p-4">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#0055FF]" /></div>
          ) : images.length === 0 ? (
            <p className="text-sm text-[#71717A] text-center py-10">Todavía no generaste ninguna imagen.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3" data-testid="generated-images-grid">
              {images.map((img) => (
                <button key={img.id} onClick={() => selectImage(img)} data-testid={`generated-image-${img.id}`}
                  className={`rounded-sm overflow-hidden border-2 ${active?.id === img.id ? "border-[#0055FF]" : "border-transparent"}`}>
                  <img src={img.variant_urls[img.selected_variant_index || 0]} alt={img.prompt} className="aspect-square object-cover w-full" />
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
